import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Globe, Layout, ShoppingCart, Code2, 
  Sparkles, ArrowRight, Check,
  ChevronLeft, ChevronRight, DollarSign, IndianRupee 
} from "lucide-react";
import { useAdmin } from "../context/AdminContext";

export default function WebServices() {
  const [currency, setCurrency] = useState("INR"); // INR or USD
  const { webPlans: adminWebPlans } = useAdmin();

  const defaultWebPlans = [
    {
      title: "Essential Web",
      subTitle: "WordPress / Static",
      inrPrice: "9,999",
      usdPrice: "149",
      icon: <Globe className="text-[#2563EB] dark:text-[#3B82F6]" size={22} />,
      link: "https://hickter.com/plansapply/all-plans/website-development-standard-wordpress/",
      features: ["Custom Responsive Theme", "Mobile & SEO Friendly", "Contact Forms & Maps", "SSL & Domain Setup", "Fast Loading Speed"],
      techStack: ["WordPress", "HTML5/CSS3", "PHP"]
    },
    {
      title: "Custom React Portal",
      subTitle: "Full Stack Web App",
      inrPrice: "24,999",
      usdPrice: "349",
      icon: <Layout className="text-[#2563EB] dark:text-[#3B82F6]" size={22} />,
      link: "https://hickter.com/plansapply/all-plans/website-development-custom-react-web-app/",
      features: ["React.js Single Page App", "Dynamic API Integrations", "Custom Admin Dashboard", "High Performance Architecture", "Automated Backups"],
      techStack: ["React", "Node.js", "Tailwind"]
    },
    {
      title: "E-Commerce Suite",
      subTitle: "Shopify / WooCommerce",
      inrPrice: "34,999",
      usdPrice: "499",
      icon: <ShoppingCart className="text-[#2563EB] dark:text-[#3B82F6]" size={22} />,
      link: "https://hickter.com/plansapply/all-plans/website-development-e-commerce-store/",
      features: ["Turnkey Store Setup", "App Store Integration", "Pro Shopify Templates", "Sales Analytics Panel", "24/7 Priority Support"],
      techStack: ["Shopify", "Liquid", "Polaris"]
    },
    {
      title: "Enterprise",
      subTitle: "Next-Gen Stack",
      inrPrice: "50,000",
      usdPrice: "779",
      icon: <Code2 className="text-[#2563EB] dark:text-[#3B82F6]" size={22} />,
      link: "https://hickter.com/plansapply/all-plans/website-development-advanced-react-next-js/",
      features: ["SSR & ISR Architecture", "Custom API Integration", "Scalable Database Setup", "Ultra Cloud Security", "Cloud-Native Ready"],
      techStack: ["Next.js", "TypeScript", "Prisma"]
    }
  ];

  const sourcePlans = adminWebPlans && adminWebPlans.length > 0 ? adminWebPlans : defaultWebPlans;
  const webPlans = sourcePlans.filter((p) => p.isVisible !== false);

  const [cardsPerSlide, setCardsPerSlide] = useState(2);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setCardsPerSlide(1); // 1 single card slide on Mobile
      } else {
        setCardsPerSlide(2); // 2 cards on Tablet & Desktop
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const totalSlides = Math.max(1, Math.ceil(webPlans.length / cardsPerSlide));
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    setCurrentSlide(0);
  }, [cardsPerSlide]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % totalSlides);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);

  const visiblePlans = webPlans.slice(currentSlide * cardsPerSlide, (currentSlide + 1) * cardsPerSlide);

  return (
    <div className="w-full max-w-[100vw] overflow-hidden py-16 px-4 md:py-24 lg:py-28 bg-[#F8FAFC] dark:bg-[#0B0F19] text-[#0F172A] dark:text-[#F8FAFC] font-sans flex items-center justify-center transition-colors duration-300 relative">
      
      {/* Outer Constrained Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex flex-col justify-center items-center relative z-10">
        
        {/* Section Header */}
        <header className="max-w-3xl mx-auto text-center mb-10 lg:mb-12 flex flex-col items-center">
          <motion.div 
            initial={{ opacity: 0, y: -12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#2563EB]/10 dark:bg-[#3B82F6]/15 border border-[#2563EB]/20 dark:border-[#3B82F6]/30 text-[#2563EB] dark:text-[#3B82F6] mb-4"
          >
            <Sparkles size={13} />
            <span className="text-xs font-bold uppercase tracking-wider">
              Website Engineering Tiers
            </span>
          </motion.div>

          <motion.h2 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-none text-[#0F172A] dark:text-[#F8FAFC] mb-6"
          >
            Website <span className="text-[#2563EB] dark:text-[#3B82F6]">Development</span>
          </motion.h2>

          {/* HIGHLY VISIBLE GLASSY CURRENCY SWITCHER BUTTONS (INR ₹ / USD $) */}
          <div className="inline-flex items-center gap-2 p-1.5 rounded-2xl bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border border-slate-300 dark:border-slate-700 shadow-lg shadow-blue-500/10">
            <button
              onClick={() => setCurrency("INR")}
              className={`h-9 px-4 text-xs sm:text-sm font-bold rounded-xl transition-all duration-200 flex items-center gap-1.5 cursor-pointer ${
                currency === "INR"
                  ? "bg-blue-600 dark:bg-blue-500 !text-white font-extrabold shadow-md shadow-blue-500/30 border border-blue-600 dark:border-blue-400"
                  : "text-slate-800 dark:text-slate-200 bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 hover:text-blue-700 dark:hover:text-blue-400 border border-slate-300 dark:border-slate-700"
              }`}
            >
              <IndianRupee size={14} className={currency === "INR" ? "!text-white" : "text-blue-600 dark:text-blue-400"} />
              <span className={currency === "INR" ? "!text-white font-extrabold" : "text-slate-800 dark:text-slate-200 font-bold"}>INR (₹)</span>
            </button>

            <button
              onClick={() => setCurrency("USD")}
              className={`h-9 px-4 text-xs sm:text-sm font-bold rounded-xl transition-all duration-200 flex items-center gap-1.5 cursor-pointer ${
                currency === "USD"
                  ? "bg-blue-600 dark:bg-blue-500 !text-white font-extrabold shadow-md shadow-blue-500/30 border border-blue-600 dark:border-blue-400"
                  : "text-slate-800 dark:text-slate-200 bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 hover:text-blue-700 dark:hover:text-blue-400 border border-slate-300 dark:border-slate-700"
              }`}
            >
              <DollarSign size={14} className={currency === "USD" ? "!text-white" : "text-blue-600 dark:text-blue-400"} />
              <span className={currency === "USD" ? "!text-white font-extrabold" : "text-slate-800 dark:text-slate-200 font-bold"}>USD ($)</span>
            </button>
          </div>

        </header>

        {/* 2 GRID CAROUSEL SLIDER WRAPPER (1 CARD SLIDE ON MOBILE) */}
        <main className="w-full max-w-4xl mx-auto relative flex flex-col items-center">
          {/* MIDDLE FLOATING PREV BUTTON */}
          <button
            onClick={prevSlide}
            className="absolute -left-3 sm:-left-6 lg:-left-8 top-1/2 -translate-y-1/2 z-30 w-11 h-11 rounded-full border border-[#E2E8F0] dark:border-[#1F2937] bg-white dark:bg-[#111827] text-[#0F172A] dark:text-[#F8FAFC] shadow-xl hover:bg-[#2563EB] dark:hover:bg-[#3B82F6] hover:border-[#2563EB] dark:hover:border-[#3B82F6] hover:scale-110 active:scale-95 transition-all duration-200 cursor-pointer flex items-center justify-center group/btn"
            aria-label="Previous Slide"
          >
            <ChevronLeft size={22} className="text-[#2563EB] dark:text-[#3B82F6] group-hover/btn:text-white transition-colors" />
          </button>

          {/* MIDDLE FLOATING NEXT BUTTON */}
          <button
            onClick={nextSlide}
            className="absolute -right-3 sm:-right-6 lg:-right-8 top-1/2 -translate-y-1/2 z-30 w-11 h-11 rounded-full border border-[#E2E8F0] dark:border-[#1F2937] bg-white dark:bg-[#111827] text-[#0F172A] dark:text-[#F8FAFC] shadow-xl hover:bg-[#2563EB] dark:hover:bg-[#3B82F6] hover:border-[#2563EB] dark:hover:border-[#3B82F6] hover:scale-110 active:scale-95 transition-all duration-200 cursor-pointer flex items-center justify-center group/btn"
            aria-label="Next Slide"
          >
            <ChevronRight size={22} className="text-[#2563EB] dark:text-[#3B82F6] group-hover/btn:text-white transition-colors" />
          </button>

          <div className="relative w-full min-h-[460px] flex items-center justify-center px-2">
            
            <AnimatePresence mode="wait">
              <motion.div
                key={`${currentSlide}-${cardsPerSlide}`}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragEnd={(_, info) => {
                  if (info.offset.x < -50) nextSlide();
                  if (info.offset.x > 50) prevSlide();
                }}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.35, ease: "easeInOut" }}
                className={`w-full grid gap-6 lg:gap-8 items-stretch cursor-grab active:cursor-grabbing ${
                  cardsPerSlide === 1 ? "grid-cols-1 max-w-sm mx-auto" : "grid-cols-1 md:grid-cols-2"
                }`}
              >
                {visiblePlans.map((plan, idx) => {
                  const displayPrice = currency === "INR" ? plan.inrPrice : plan.usdPrice;
                  const currencySymbol = currency === "INR" ? "₹" : "$";

                  return (
                    <div
                      key={idx}
                      className="p-6 sm:p-8 rounded-2xl bg-white dark:bg-[#111827] text-[#0F172A] dark:text-[#F8FAFC] border border-[#E2E8F0] dark:border-[#1F2937] hover:border-[#2563EB] dark:hover:border-[#3B82F6] transition-all duration-300 shadow-sm flex flex-col justify-between h-full min-h-fit"
                    >
                      <div className="flex-grow flex flex-col justify-between">
                        <div>
                          {/* Card Icon & Starting Price */}
                          <div className="flex items-center justify-between pb-4 mb-4 border-b border-[#E2E8F0] dark:border-[#1F2937]">
                            <div className="p-2 sm:p-3 rounded-xl bg-[#F8FAFC] dark:bg-[#1F2937] border border-[#E2E8F0] dark:border-[#1F2937] flex items-center justify-center">
                              {plan.imageUrl ? (
                                <img src={plan.imageUrl} alt={plan.title} className="w-8 h-8 rounded-lg object-cover" />
                              ) : plan.icon ? (
                                plan.icon
                              ) : (
                                <Globe className="text-[#2563EB] dark:text-[#3B82F6]" size={22} />
                              )}
                            </div>
                            <div className="text-right">
                              <span className="block text-[10px] font-bold text-[#475569] dark:text-[#94A3B8] uppercase tracking-wider">Starts At</span>
                              <div className="flex items-baseline justify-end text-[#0F172A] dark:text-[#F8FAFC]">
                                <span className="text-xs font-bold mr-0.5 text-[#2563EB] dark:text-[#3B82F6]">{currencySymbol}</span>
                                <span className="text-2xl font-extrabold tracking-tight">{displayPrice}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center justify-between gap-2 mb-1">
                            <h3 className="text-xl font-extrabold text-[#0F172A] dark:text-[#F8FAFC] tracking-tight">
                              {plan.title}
                            </h3>
                            {plan.customBadge ? (
                              <span className="px-2.5 py-0.5 rounded-full bg-blue-600 !text-white font-black text-[10px] uppercase shrink-0">
                                {plan.customBadge}
                              </span>
                            ) : plan.popular ? (
                              <span className="px-2.5 py-0.5 rounded-full bg-blue-600 !text-white font-black text-[10px] uppercase shrink-0">
                                Most Popular
                              </span>
                            ) : null}
                          </div>
                          
                          <p className="text-xs font-bold text-[#475569] dark:text-[#94A3B8] uppercase tracking-wider mb-4">
                            {plan.subTitle}
                          </p>

                          {/* Feature List */}
                          <div className="space-y-3.5 my-6">
                            {plan.features.map((feat, i) => (
                              <div key={i} className="flex gap-3 items-center">
                                <div className="w-4 h-4 rounded-full bg-[#2563EB]/10 dark:bg-[#3B82F6]/20 flex items-center justify-center shrink-0">
                                  <Check size={11} strokeWidth={3} className="text-[#2563EB] dark:text-[#3B82F6]" />
                                </div>
                                <span className="text-xs font-semibold text-[#0F172A] dark:text-[#F8FAFC] leading-snug">
                                  {feat}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Tech Stack Pills */}
                        <div className="pt-4 border-t border-[#E2E8F0] dark:border-[#1F2937] mb-6">
                          <span className="block text-[10px] font-bold uppercase tracking-wider text-[#475569] dark:text-[#94A3B8] mb-2">Core Tech</span>
                          <div className="flex flex-wrap gap-1.5">
                            {plan.techStack.map((tech, i) => (
                              <span key={i} className="text-[10px] font-bold px-2.5 py-1 rounded-md bg-[#F8FAFC] dark:bg-[#1F2937] text-[#0F172A] dark:text-[#F8FAFC] border border-[#E2E8F0] dark:border-[#1F2937]">
                                {tech}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* PINNED CTA BUTTON */}
                      <Link
                        to={`/apply/website?plan=${encodeURIComponent(plan.title)}`}
                        className="btn-primary-blue mt-auto w-full h-12 py-3 px-4 font-semibold text-sm flex items-center justify-center gap-2 text-center cursor-pointer transition-all duration-200 !text-white"
                      >
                        <span className="!text-white font-bold">Apply Now for {plan.title}</span>
                        <ArrowRight size={14} className="!text-white" />
                      </Link>
                    </div>
                  );
                })}
              </motion.div>
            </AnimatePresence>

          </div>

          {/* CAROUSEL PAGINATION DOTS */}
          <div className="flex items-center justify-center gap-2 mt-6">
            {Array.from({ length: totalSlides }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={`h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                  currentSlide === i ? "w-8 bg-[#2563EB] dark:bg-[#3B82F6]" : "w-2.5 bg-[#CBD5E1] dark:bg-[#1F2937]"
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>

        </main>

      </div>
    </div>
  );
}